import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import {
  Send,
  Upload,
  FileSpreadsheet,
  ArrowRight,
  ArrowLeft,
  Play,
  Pause,
  RefreshCw,
  XCircle,
  List,
  Plus,
  Image,
  Video,
  FileText,
  BarChart3,
  Users,
  CheckCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import DisparoAvulsoDetalheModal from '@/components/DisparoAvulsoDetalheModal';
import ExportarDisparoAvulsoModal from '@/components/modals/ExportarDisparoAvulsoModal';
import { Download } from 'lucide-react';

const STEPS = ['Conexão e template', 'Header (opcional)', 'Upload XLSX', 'Mapeamento', 'Confirmar'];

// Valor sentinela usado no Select de conexão quando o cliente é V2 (ÁtomosChat).
// Mesmo padrão usado em src/components/business-rules/TemplateStrategiesTab.tsx.
const CONNECTION_VALUE_ATOMOS_V2 = 'atomos-v2';

interface AtomosTemplateLite {
  id: string;
  name: string;
  text?: string;
  fileType?: string;
  parameters?: Array<{ name?: string }>;
}

interface AtomosDepartmentLite {
  id: string;
  name: string;
}

interface AtomosChatbotLite {
  id: string;
  name: string;
}

export default function DisparoAvulso() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [list, setList] = useState<any[]>([]);
  const [listTotal, setListTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showVerMaisModal, setShowVerMaisModal] = useState(false);
  const [verMaisPage, setVerMaisPage] = useState(1);
  const [verMaisList, setVerMaisList] = useState<any[]>([]);
  const [verMaisTotal, setVerMaisTotal] = useState(0);
  const [verMaisLoading, setVerMaisLoading] = useState(false);
  const [connections, setConnections] = useState<{ id: number; name: string; connection_type: string }[]>([]);
  const [templates, setTemplates] = useState<{ id: string; name: string; language: string; components?: any[] }[]>([]);
  // V2 (ÁtomosChat): múltiplas configs + templates + departments do cliente.
  const [atomosConfigs, setAtomosConfigs] = useState<{ id: number; account_name: string; channel_name?: string | null; channel_phone?: string | null; is_active?: boolean }[]>([]);
  const [envioVersionPref, setEnvioVersionPref] = useState<'v1' | 'v2'>('v1');
  const [selectedAtomosConfigId, setSelectedAtomosConfigId] = useState<number | null>(null);
  const [atomosTemplates, setAtomosTemplates] = useState<AtomosTemplateLite[]>([]);
  const [atomosDepartments, setAtomosDepartments] = useState<AtomosDepartmentLite[]>([]);
  const activeAtomosConfigs = atomosConfigs.filter((c) => c.is_active !== false);
  const atomosConfig = (envioVersionPref === 'v2' && activeAtomosConfigs.length > 0)
    ? { envioVersion: 'v2' as const, config: activeAtomosConfigs[0] }
    : null;
  const [atomosChatbots, setAtomosChatbots] = useState<AtomosChatbotLite[]>([]);
  const [selectedAtomosTemplate, setSelectedAtomosTemplate] = useState<AtomosTemplateLite | null>(null);
  const [selectedAtomosDepartmentId, setSelectedAtomosDepartmentId] = useState<string>('');
  const [selectedAtomosChatbotId, setSelectedAtomosChatbotId] = useState<string>('');
  const [showWizard, setShowWizard] = useState(false);
  const [step, setStep] = useState(0);
  // connectionId aceita número (V1 Meta), 'atomos-v2' (V2) ou '' (não selecionado).
  const [connectionId, setConnectionId] = useState<number | typeof CONNECTION_VALUE_ATOMOS_V2 | ''>('');
  const [templateName, setTemplateName] = useState('');
  const [templateHeader, setTemplateHeader] = useState<{ hasHeader: boolean; type?: 'image' | 'video' | 'document' }>({ hasHeader: false });
  const [headerSource, setHeaderSource] = useState<'none' | 'fixed' | 'column'>('none');
  const [headerFixedUrl, setHeaderFixedUrl] = useState('');
  const [headerColumnName, setHeaderColumnName] = useState('');
  const [headerFixedFilename, setHeaderFixedFilename] = useState('');
  const [headerFilenameColumn, setHeaderFilenameColumn] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<{
    headers: string[];
    totalRows: number;
    sampleRows: Record<string, unknown>[];
    expectedVariables: number;
    suggestedMapping: Record<string, number>;
    templateHeader: { hasHeader: boolean; type?: 'image' | 'video' | 'document' };
    templateBodyText?: string | null;
    /** Nomes ordenados das variáveis do template V2 (ex.: ['nome','placa']). Vazio em V1. */
    parameterNames?: string[];
    envioVersion?: 'v1' | 'v2';
  } | null>(null);
  const [columnMapping, setColumnMapping] = useState<Record<string, number>>({});
  const [creating, setCreating] = useState(false);
  const [selectedDisparo, setSelectedDisparo] = useState<any | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [exportTarget, setExportTarget] = useState<any | null>(null);
  // Superadmin: seleção de cliente para puxar conexões/templates do cliente
  const [clients, setClients] = useState<{ id: number; legal_name?: string; trade_name?: string }[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | ''>('');
  const [loadingClients, setLoadingClients] = useState(false);
  // Filtros na listagem (admin: por cliente; todos: por status)
  const [listFilterClientId, setListFilterClientId] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const isAdmin = user?.role === 'admin';
  const effectiveClientId = isAdmin && selectedClientId ? selectedClientId : (user?.client_id ?? user?.id);

  useEffect(() => {
    if (!isAdmin) loadConnections();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      setLoadingClients(true);
      api.getClients()
        .then((data) => setClients(Array.isArray(data) ? data : []))
        .catch(() => setClients([]))
        .finally(() => setLoadingClients(false));
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin && showWizard) {
      setLoadingClients(true);
      api.getClients()
        .then((data) => setClients(Array.isArray(data) ? data : []))
        .catch(() => setClients([]))
        .finally(() => setLoadingClients(false));
    }
  }, [isAdmin, showWizard]);

  useEffect(() => {
    if (isAdmin && selectedClientId) {
      loadConnections(selectedClientId);
      setConnectionId('');
      setTemplateName('');
      setTemplates([]);
    }
  }, [isAdmin, selectedClientId]);

  useEffect(() => {
    if (!connectionId || !showWizard) return;
    if (connectionId === CONNECTION_VALUE_ATOMOS_V2) {
      // Auto-selecionar quando há apenas uma config V2 ativa
      if (selectedAtomosConfigId == null && activeAtomosConfigs.length === 1) {
        setSelectedAtomosConfigId(activeAtomosConfigs[0].id);
        return; // useEffect dispara de novo após o set
      }
      // Só carrega templates/equipes/chatbots quando há uma config selecionada (ou só uma disponível)
      if (selectedAtomosConfigId != null || activeAtomosConfigs.length <= 1) {
        loadAtomosTemplates();
        loadAtomosDepartments();
        loadAtomosChatbots();
      }
    } else {
      loadTemplates(Number(connectionId));
    }
  }, [connectionId, showWizard, selectedAtomosConfigId]);

  const LIST_PAGE_SIZE = 10;
  const VER_MAIS_PAGE_SIZE = 50;

  const loadList = async () => {
    try {
      setLoading(true);
      const params: { client_id?: number; status?: string; limit?: number; offset?: number } = {};
      if (isAdmin && listFilterClientId) params.client_id = listFilterClientId;
      else if (!isAdmin && effectiveClientId) params.client_id = effectiveClientId;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      params.limit = LIST_PAGE_SIZE;
      params.offset = 0;
      const res = await api.disparoAvulsoList(params);
      setList(res.list || []);
      setListTotal(res.total ?? 0);
    } catch (e: any) {
      toast({ title: 'Erro', description: e?.message || 'Erro ao listar', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const loadVerMaisPage = async (page: number) => {
    try {
      setVerMaisLoading(true);
      const params: { client_id?: number; status?: string; limit?: number; offset?: number } = {};
      if (isAdmin && listFilterClientId) params.client_id = listFilterClientId;
      else if (!isAdmin && effectiveClientId) params.client_id = effectiveClientId;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      params.limit = VER_MAIS_PAGE_SIZE;
      params.offset = (page - 1) * VER_MAIS_PAGE_SIZE;
      const res = await api.disparoAvulsoList(params);
      setVerMaisList(res.list || []);
      setVerMaisTotal(res.total ?? 0);
      setVerMaisPage(page);
    } catch (e: any) {
      toast({ title: 'Erro', description: e?.message || 'Erro ao carregar', variant: 'destructive' });
    } finally {
      setVerMaisLoading(false);
    }
  };

  const openVerMaisModal = () => {
    setShowVerMaisModal(true);
    setVerMaisPage(1);
    loadVerMaisPage(1);
  };

  useEffect(() => {
    if (!showWizard) loadList();
  }, [listFilterClientId, statusFilter]);

  const loadConnections = async (forClientId?: number) => {
    // V1: lista conexões Meta. V2: tenta carregar config ÁtomosChat — se ativa,
    // o select do wizard mostra a opção "ÁtomosChat (Envios V2)".
    const cid = forClientId ?? effectiveClientId;
    try {
      const url = forClientId != null ? `/whatsapp/client/${forClientId}` : '/whatsapp/client/current';
      const [res, configsRes, versionRes] = await Promise.all([
        api.get(url).catch(() => null),
        cid != null
          ? api.get(`/atomos-chat/client/${cid}/configs`).catch(() => null) as Promise<{ success?: boolean; configs?: any[] } | null>
          : Promise.resolve(null),
        cid != null
          ? api.get(`/atomos-chat/client/${cid}/envio-version`).catch(() => null) as Promise<{ success?: boolean; envio_version?: 'v1' | 'v2' } | null>
          : Promise.resolve(null),
      ]);
      const conns = (res as any)?.connections?.filter((c: any) => c.is_active) || [];
      setConnections(conns);
      const v2Configs = Array.isArray(configsRes?.configs) ? configsRes!.configs! : [];
      setAtomosConfigs(v2Configs as any);
      setEnvioVersionPref((versionRes?.envio_version === 'v2' ? 'v2' : 'v1'));
      // Pré-selecionar a primeira config ativa
      const firstActive = v2Configs.find((c: any) => c.is_active !== false);
      setSelectedAtomosConfigId(firstActive?.id ?? null);
    } catch (_) {
      setConnections([]);
      setAtomosConfigs([]);
      setEnvioVersionPref('v1');
      setSelectedAtomosConfigId(null);
    }
  };

  const loadTemplates = async (connId: number) => {
    try {
      // Templates da Meta vinculados à conexão WhatsApp (não meta-api/tags)
      const res = await api.get(`/whatsapp/${connId}/templates`);
      const data = (res as any).templates ?? (res as any).data ?? [];
      setTemplates(Array.isArray(data) ? data : []);
    } catch (_) {
      setTemplates([]);
    }
  };

  const loadAtomosTemplates = async () => {
    if (!effectiveClientId) return;
    try {
      const url = selectedAtomosConfigId
        ? `/atomos-chat/client/${effectiveClientId}/templates?config_id=${selectedAtomosConfigId}`
        : `/atomos-chat/client/${effectiveClientId}/templates`;
      const res = await api.get(url);
      const list = ((res as any)?.templates ?? (res as any)?.data ?? []) as AtomosTemplateLite[];
      setAtomosTemplates(Array.isArray(list) ? list : []);
    } catch (_) {
      setAtomosTemplates([]);
    }
  };

  const loadAtomosDepartments = async () => {
    if (!effectiveClientId) return;
    try {
      const url = selectedAtomosConfigId
        ? `/atomos-chat/client/${effectiveClientId}/departments?config_id=${selectedAtomosConfigId}`
        : `/atomos-chat/client/${effectiveClientId}/departments`;
      const res = await api.get(url);
      const list = ((res as any)?.departments ?? (res as any)?.data ?? []) as AtomosDepartmentLite[];
      setAtomosDepartments(Array.isArray(list) ? list : []);
    } catch (_) {
      setAtomosDepartments([]);
    }
  };

  const loadAtomosChatbots = async () => {
    if (!effectiveClientId) return;
    try {
      const res = await api.get(`/atomos-chat/client/${effectiveClientId}/chatbots`);
      const list = ((res as any)?.chatbots ?? (res as any)?.data ?? []) as AtomosChatbotLite[];
      setAtomosChatbots(Array.isArray(list) ? list : []);
    } catch (_) {
      setAtomosChatbots([]);
    }
  };

  const isV2 = connectionId === CONNECTION_VALUE_ATOMOS_V2;

  const handlePreview = async () => {
    if (!file || !connectionId) {
      toast({ title: 'Campos obrigatórios', description: 'Selecione conexão, template e arquivo.', variant: 'destructive' });
      return;
    }
    if (isV2 && !selectedAtomosTemplate?.id) {
      toast({ title: 'Template obrigatório', description: 'Selecione um template ÁtomosChat.', variant: 'destructive' });
      return;
    }
    if (isV2 && activeAtomosConfigs.length > 1 && !selectedAtomosConfigId) {
      toast({ title: 'Conexão V2 obrigatória', description: 'Selecione qual conexão V2 enviará as mensagens.', variant: 'destructive' });
      return;
    }
    if (!isV2 && !templateName) {
      toast({ title: 'Template obrigatório', description: 'Selecione um template Meta.', variant: 'destructive' });
      return;
    }
    try {
      const form = new FormData();
      form.append('file', file);
      // V2: usa o nome do template ÁtomosChat (apenas para exibição na lista). V1: nome do template Meta.
      const finalTemplateName = isV2 ? (selectedAtomosTemplate?.name || '') : templateName;
      form.append('template_name', finalTemplateName);
      if (isV2) {
        form.append('atomos_template_id', selectedAtomosTemplate!.id);
        if (selectedAtomosConfigId) {
          form.append('atomos_chat_config_id', String(selectedAtomosConfigId));
        }
      } else {
        form.append('connection_id', String(connectionId));
      }
      if (effectiveClientId) form.append('client_id', String(effectiveClientId));
      const res = await api.disparoAvulsoPreviewXlsx(form);
      setPreview({
        headers: res.headers || [],
        totalRows: res.totalRows || 0,
        sampleRows: res.sampleRows || [],
        expectedVariables: res.expectedVariables || 0,
        suggestedMapping: res.suggestedMapping || {},
        templateHeader: res.templateHeader || { hasHeader: false },
        templateBodyText: (res as { templateBodyText?: string | null }).templateBodyText ?? null,
        parameterNames: (res as any)?.parameterNames ?? [],
        envioVersion: (res as any)?.envioVersion ?? (isV2 ? 'v2' : 'v1'),
      });
      setColumnMapping(res.suggestedMapping || {});
      setTemplateHeader(res.templateHeader || { hasHeader: false });
      setStep(3);
    } catch (e: any) {
      toast({ title: 'Erro no preview', description: e?.message || 'Erro ao processar arquivo', variant: 'destructive' });
    }
  };

  const handleCreate = async () => {
    if (!file || !connectionId || !columnMapping || Object.keys(columnMapping).length === 0) {
      toast({ title: 'Dados incompletos', description: 'Preencha mapeamento e envie o arquivo.', variant: 'destructive' });
      return;
    }
    if (isV2 && !selectedAtomosTemplate?.id) {
      toast({ title: 'Template obrigatório', description: 'Selecione um template ÁtomosChat.', variant: 'destructive' });
      return;
    }
    if (!isV2 && !templateName) {
      toast({ title: 'Template obrigatório', description: 'Selecione um template Meta.', variant: 'destructive' });
      return;
    }
    try {
      setCreating(true);
      const form = new FormData();
      form.append('file', file);
      const finalTemplateName = isV2 ? (selectedAtomosTemplate?.name || '') : templateName;
      form.append('template_name', finalTemplateName);
      form.append('template_language', 'pt_BR');
      form.append('column_mapping', JSON.stringify(columnMapping));

      if (isV2) {
        // V2: connection_id null + atomos_*. Backend infere caminho V2 pela presença de atomos_template_id.
        form.append('atomos_template_id', selectedAtomosTemplate!.id);
        if (selectedAtomosConfigId) {
          form.append('atomos_chat_config_id', String(selectedAtomosConfigId));
        }
        if (selectedAtomosTemplate!.fileType) {
          form.append('atomos_file_type', selectedAtomosTemplate!.fileType);
        }
        if (selectedAtomosDepartmentId) {
          const dept = atomosDepartments.find((d) => d.id === selectedAtomosDepartmentId);
          if (dept) {
            form.append('atomos_department_id', dept.id);
            form.append('atomos_department_name', dept.name);
          }
        }
        if (selectedAtomosChatbotId) {
          const bot = atomosChatbots.find((c) => c.id === selectedAtomosChatbotId);
          if (bot) {
            form.append('atomos_chatbot_id', bot.id);
            form.append('atomos_chatbot_name', bot.name);
          }
        }
      } else {
        form.append('whatsapp_connection_id', String(connectionId));
      }

      if (templateHeader.hasHeader && templateHeader.type) {
        form.append('header_type', templateHeader.type);
        form.append('header_source', headerSource);
        if (headerSource === 'fixed') {
          form.append('header_fixed_url', headerFixedUrl);
          if (templateHeader.type === 'document') form.append('header_fixed_filename', headerFixedFilename);
        } else if (headerSource === 'column') {
          form.append('header_column_name', headerColumnName);
          if (templateHeader.type === 'document' && headerFilenameColumn) form.append('header_filename_column', headerFilenameColumn);
        }
      }
      if (effectiveClientId) form.append('client_id', String(effectiveClientId));

      const CREATE_TIMEOUT_MS = 120_000; // 2 minutos
      const res = await Promise.race([
        api.disparoAvulsoCreate(form),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('A requisição demorou muito. Verifique a conexão e a planilha, e tente novamente.')), CREATE_TIMEOUT_MS)
        ),
      ]);

      if (res && typeof (res as any).created !== 'number') {
        throw new Error((res as any).error || 'Resposta inválida do servidor');
      }
      toast({ title: 'Disparo criado', description: `${res.created} contatos importados.` });
      setShowWizard(false);
      resetWizard();
      loadList();
      if (res.disparo?.id) {
        setSelectedDisparo(res.disparo);
        setShowDetailModal(true);
      }
    } catch (e: any) {
      toast({ title: 'Erro ao criar', description: e?.message || 'Erro', variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  // Helper para encontrar o client_id de um disparo a partir das listas em cache
  const findDisparoClientId = (id: number): number | null | undefined => {
    if (selectedDisparo?.id === id) return selectedDisparo.client_id;
    return (
      list.find((d) => d.id === id)?.client_id ??
      verMaisList.find((d) => d.id === id)?.client_id
    );
  };

  const handleIniciar = async (id: number) => {
    const clientId = findDisparoClientId(id);
    if (clientId == null) {
      toast({ title: 'Erro', description: 'Cliente do disparo não encontrado.', variant: 'destructive' });
      return;
    }
    try {
      await api.disparoAvulsoIniciar(id, clientId);
      toast({ title: 'Envio iniciado', description: 'Mensagens entrando na fila.' });
      loadList();
    } catch (e: any) {
      toast({ title: 'Erro', description: e?.message || 'Erro ao iniciar', variant: 'destructive' });
    }
  };

  const handleCancelar = async (id: number) => {
    const clientId = findDisparoClientId(id);
    if (clientId == null) {
      toast({ title: 'Erro', description: 'Cliente do disparo não encontrado.', variant: 'destructive' });
      return;
    }
    try {
      await api.disparoAvulsoCancelar(id, clientId);
      toast({ title: 'Disparo cancelado' });
      loadList();
      if (selectedDisparo?.id === id) {
        setSelectedDisparo(null);
        setShowDetailModal(false);
      }
    } catch (e: any) {
      toast({ title: 'Erro', description: e?.message, variant: 'destructive' });
    }
  };

  const handlePausar = async (id: number) => {
    const clientId = findDisparoClientId(id);
    if (clientId == null) {
      toast({ title: 'Erro', description: 'Cliente do disparo não encontrado.', variant: 'destructive' });
      return;
    }
    try {
      await api.disparoAvulsoPausar(id, clientId);
      toast({ title: 'Disparo pausado' });
      loadList();
    } catch (e: any) {
      toast({ title: 'Erro', description: e?.message || 'Erro ao pausar', variant: 'destructive' });
    }
  };

  const handleRetomar = async (id: number) => {
    const clientId = findDisparoClientId(id);
    if (clientId == null) {
      toast({ title: 'Erro', description: 'Cliente do disparo não encontrado.', variant: 'destructive' });
      return;
    }
    try {
      await api.disparoAvulsoRetomar(id, clientId);
      toast({ title: 'Disparo retomado', description: 'Mensagens voltando para a fila.' });
      loadList();
    } catch (e: any) {
      toast({ title: 'Erro', description: e?.message || 'Erro ao retomar', variant: 'destructive' });
    }
  };

  const handleReenviarFalhas = async (id: number) => {
    const clientId = findDisparoClientId(id);
    if (clientId == null) {
      toast({ title: 'Erro', description: 'Cliente do disparo não encontrado.', variant: 'destructive' });
      return;
    }
    try {
      await api.disparoAvulsoReenviarFalhas(id, clientId);
      toast({ title: 'Reenviando falhas', description: 'Contatos com falha voltaram para a fila.' });
      loadList();
    } catch (e: any) {
      toast({ title: 'Erro', description: e?.message || 'Erro ao reenviar falhas', variant: 'destructive' });
    }
  };

  const openDetailModal = (d: any) => {
    setSelectedDisparo(d);
    setShowDetailModal(true);
  };

  const openExportModal = (d: any) => {
    setExportTarget(d);
  };

  const resetWizard = () => {
    setStep(0);
    if (isAdmin) setSelectedClientId('');
    setConnectionId('');
    setTemplateName('');
    setSelectedAtomosTemplate(null);
    setSelectedAtomosDepartmentId('');
    setSelectedAtomosChatbotId('');
    setAtomosTemplates([]);
    setAtomosDepartments([]);
    setAtomosChatbots([]);
    setTemplateHeader({ hasHeader: false });
    setHeaderSource('none');
    setHeaderFixedUrl('');
    setHeaderColumnName('');
    setHeaderFixedFilename('');
    setHeaderFilenameColumn('');
    setFile(null);
    setPreview(null);
    setColumnMapping({});
  };

  const phoneColumn = preview?.headers?.[0];
  const varColumns = preview?.headers?.filter((h) => h !== phoneColumn) || [];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Disparo Avulso</h1>
            <p className="text-muted-foreground">
              Envie mensagens em massa via template Meta com lista XLSX (celular + variáveis) e registro no ChatConnect.
            </p>
          </div>
          {!showWizard && (
            <Button onClick={() => setShowWizard(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Novo disparo
            </Button>
          )}
        </div>

        {showWizard ? (
          <Card>
            <CardHeader>
              <CardTitle>Novo disparo avulso</CardTitle>
              <CardDescription>Passo {step + 1} de {STEPS.length}: {STEPS[step]}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Step 0: Cliente (admin) + Conexão e template */}
              {step === 0 && (
                <>
                  <div className="grid gap-4">
                    {isAdmin && (
                      <>
                        <Label>Cliente</Label>
                        <Select
                          value={selectedClientId ? String(selectedClientId) : ''}
                          onValueChange={(v) => setSelectedClientId(v ? Number(v) : '')}
                          disabled={loadingClients}
                        >
                          <SelectTrigger><SelectValue placeholder={loadingClients ? 'Carregando...' : 'Selecione o cliente'} /></SelectTrigger>
                          <SelectContent>
                            {clients.map((c) => (
                              <SelectItem key={c.id} value={String(c.id)}>
                                {(c as any).trade_name || (c as any).legal_name || `Cliente ${c.id}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </>
                    )}
                    <Label>Conexão WhatsApp</Label>
                    <Select
                      value={String(connectionId)}
                      onValueChange={(v) => {
                        if (!v) {
                          setConnectionId('');
                          return;
                        }
                        if (v === CONNECTION_VALUE_ATOMOS_V2) {
                          setConnectionId(CONNECTION_VALUE_ATOMOS_V2);
                          setTemplateName('');
                        } else {
                          setConnectionId(Number(v));
                          setSelectedAtomosTemplate(null);
                          setSelectedAtomosDepartmentId('');
                          setSelectedAtomosChatbotId('');
                        }
                      }}
                      disabled={isAdmin && !selectedClientId}
                    >
                      <SelectTrigger><SelectValue placeholder={isAdmin && !selectedClientId ? 'Selecione o cliente primeiro' : 'Selecione'} /></SelectTrigger>
                      <SelectContent>
                        {atomosConfig?.envioVersion === 'v2' && (
                          <SelectItem value={CONNECTION_VALUE_ATOMOS_V2}>
                            ÁtomosChat (Envios V2)
                          </SelectItem>
                        )}
                        {connections.map((c) => (
                          <SelectItem key={c.id} value={String(c.id)}>{c.name} ({c.connection_type})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Sub-seletor V2: qual conexão ÁtomosChat enviará */}
                    {connectionId === CONNECTION_VALUE_ATOMOS_V2 && activeAtomosConfigs.length > 1 && (
                      <>
                        <Label>Conexão V2 (ÁtomosChat)</Label>
                        <Select
                          value={selectedAtomosConfigId ? String(selectedAtomosConfigId) : ''}
                          onValueChange={(v) => {
                            const id = v ? Number(v) : null;
                            setSelectedAtomosConfigId(id);
                            // Recarrega templates e equipes da conexão selecionada
                            setSelectedAtomosTemplate(null);
                            setSelectedAtomosDepartmentId('');
                            setAtomosTemplates([]);
                            setAtomosDepartments([]);
                            // useEffect dispara loadAtomosTemplates/Departments via mudança de selectedAtomosConfigId
                          }}
                        >
                          <SelectTrigger><SelectValue placeholder="Selecione a conexão V2" /></SelectTrigger>
                          <SelectContent>
                            {activeAtomosConfigs.map((cfg) => (
                              <SelectItem key={cfg.id} value={String(cfg.id)}>
                                {cfg.account_name}{cfg.channel_phone ? ` — ${cfg.channel_phone}` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </>
                    )}

                    {/* Template — V1 (Meta) ou V2 (ÁtomosChat) conforme conexão escolhida. */}
                    {connectionId === CONNECTION_VALUE_ATOMOS_V2 ? (
                      <>
                        <Label>Template ÁtomosChat</Label>
                        <Select
                          value={selectedAtomosTemplate?.id || ''}
                          onValueChange={(v) => {
                            const tpl = atomosTemplates.find((t) => t.id === v) || null;
                            setSelectedAtomosTemplate(tpl);
                          }}
                        >
                          <SelectTrigger><SelectValue placeholder="Selecione o template V2" /></SelectTrigger>
                          <SelectContent>
                            {atomosTemplates.map((t) => (
                              <SelectItem key={t.id} value={t.id}>
                                {t.name}{t.fileType ? ` · ${t.fileType}` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {atomosDepartments.length > 0 && (
                          <>
                            <Label>Equipe (opcional)</Label>
                            <Select
                              value={selectedAtomosDepartmentId || '__none__'}
                              onValueChange={(v) => setSelectedAtomosDepartmentId(v === '__none__' ? '' : v)}
                            >
                              <SelectTrigger><SelectValue placeholder="Sem equipe atribuída" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">Sem equipe atribuída</SelectItem>
                                {atomosDepartments.map((d) => (
                                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </>
                        )}

                        {atomosChatbots.length > 0 && (
                          <>
                            <Label>Fluxo (opcional)</Label>
                            <Select
                              value={selectedAtomosChatbotId || '__none__'}
                              onValueChange={(v) => setSelectedAtomosChatbotId(v === '__none__' ? '' : v)}
                            >
                              <SelectTrigger><SelectValue placeholder="Sem fluxo" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">Sem fluxo</SelectItem>
                                {atomosChatbots.map((c) => (
                                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <Label>Template Meta</Label>
                        <Select value={templateName} onValueChange={setTemplateName}>
                          <SelectTrigger><SelectValue placeholder="Selecione o template" /></SelectTrigger>
                          <SelectContent>
                            {templates.map((t) => (
                              <SelectItem key={t.id || t.name} value={t.name}>{t.name} ({t.language})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </>
                    )}
                  </div>
                  <div className="flex justify-end">
                    <Button
                      onClick={() => setStep(1)}
                      disabled={
                        !connectionId ||
                        (isAdmin && !selectedClientId) ||
                        (connectionId === CONNECTION_VALUE_ATOMOS_V2
                          ? (!selectedAtomosTemplate || (activeAtomosConfigs.length > 1 && !selectedAtomosConfigId))
                          : !templateName)
                      }
                    >
                      Próximo <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </>
              )}

              {/* Step 1: Header */}
              {step === 1 && (
                <>
                  <p className="text-sm text-muted-foreground">
                    O template possui header de mídia? Será detectado no preview. Avance para upload.
                  </p>
                  <div className="flex gap-4">
                    <Button variant="outline" onClick={() => setStep(0)}><ArrowLeft className="mr-2 h-4 w-4" /> Voltar</Button>
                    <Button onClick={() => setStep(2)}>Próximo: Upload XLSX <ArrowRight className="ml-2 h-4 w-4" /></Button>
                  </div>
                </>
              )}

              {/* Step 2: Upload XLSX */}
              {step === 2 && (
                <>
                  <div className="space-y-4">
                    <Label>Arquivo XLSX</Label>
                    <p className="text-sm text-muted-foreground">
                      Primeira coluna = celular (obrigatório). Demais colunas = variáveis do template.
                    </p>
                    <Input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                    />
                    {file && <p className="text-sm">{file.name}</p>}
                  </div>
                  <div className="flex gap-4">
                    <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="mr-2 h-4 w-4" /> Voltar</Button>
                    <Button onClick={handlePreview} disabled={!file}>
                      <FileSpreadsheet className="mr-2 h-4 w-4" /> Preview e mapeamento
                    </Button>
                  </div>
                </>
              )}

              {/* Step 3: Mapeamento */}
              {step === 3 && preview && (
                <>
                  <div className="space-y-4">
                    {/* Preview completo do template */}
                    {(preview.templateBodyText != null && preview.templateBodyText !== '') && (
                      <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
                        <p className="font-medium text-sm">Preview do template</p>
                        <p className="text-sm text-muted-foreground">Corpo da mensagem (como está na Meta):</p>
                        <div className="rounded bg-background p-3 text-sm whitespace-pre-wrap border">
                          {preview.templateBodyText}
                        </div>
                        {preview.sampleRows.length > 0 && Object.keys(columnMapping).length > 0 && (
                          <>
                            <p className="text-sm text-muted-foreground pt-2">Exemplo com a primeira linha da planilha:</p>
                            <div className="rounded bg-background p-3 text-sm whitespace-pre-wrap border">
                              {(() => {
                                const row = preview.sampleRows[0] as Record<string, unknown>;
                                let text = preview.templateBodyText || '';
                                const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                                for (let v = 1; v <= preview.expectedVariables; v++) {
                                  const colName = Object.keys(columnMapping).find((k) => columnMapping[k] === v);
                                  const val = colName && row[colName] != null ? String(row[colName]).trim() : '';
                                  const placeholder = val || `<${colName || '?'}>`;
                                  // V1 Meta: {{1}}, {{2}}...
                                  text = text.replace(new RegExp(`\\{\\{${v}\\}\\}`, 'g'), () => placeholder);
                                  // V2 ÁtomosChat: ${nome}, {nome} ou [NOME] (case-insensitive) usando parameterNames.
                                  const pname = preview.parameterNames?.[v - 1];
                                  if (pname) {
                                    const esc = escapeRegex(pname);
                                    text = text.replace(new RegExp(`\\$?\\{${esc}\\}`, 'gi'), () => placeholder);
                                    text = text.replace(new RegExp(`\\[${esc}\\]`, 'gi'), () => placeholder);
                                  }
                                }
                                return text;
                              })()}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    <p><strong>Total de linhas:</strong> {preview.totalRows}. <strong>Variáveis do template:</strong> {preview.expectedVariables}.</p>
                    {preview.templateHeader.hasHeader && (
                      <div className="rounded border p-4 space-y-2">
                        <p className="font-medium">Header do template ({preview.templateHeader.type})</p>
                        <div className="flex gap-4 items-center">
                          <label className="flex items-center gap-2">
                            <input type="radio" checked={headerSource === 'none'} onChange={() => setHeaderSource('none')} />
                            Sem header
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="radio" checked={headerSource === 'fixed'} onChange={() => setHeaderSource('fixed')} />
                            Link fixo
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="radio" checked={headerSource === 'column'} onChange={() => setHeaderSource('column')} />
                            Coluna da planilha
                          </label>
                        </div>
                        {headerSource === 'fixed' && (
                          <Input placeholder="URL do header" value={headerFixedUrl} onChange={(e) => setHeaderFixedUrl(e.target.value)} />
                        )}
                        {headerSource === 'column' && (
                          <Select value={headerColumnName} onValueChange={setHeaderColumnName}>
                            <SelectTrigger className="w-48"><SelectValue placeholder="Coluna" /></SelectTrigger>
                            <SelectContent>
                              {varColumns.map((h) => (
                                <SelectItem key={h} value={h}>{h}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {preview.templateHeader.type === 'document' && headerSource === 'fixed' && (
                          <Input placeholder="Nome do arquivo (opcional)" value={headerFixedFilename} onChange={(e) => setHeaderFixedFilename(e.target.value)} />
                        )}
                      </div>
                    )}
                    <p className="font-medium">
                      Mapeamento: primeira coluna = celular. Associe cada variável a uma coluna da planilha.
                      {preview.expectedVariables === 0 && (
                        <span className="block text-xs text-amber-600 mt-1">
                          Nenhuma variável detectada no template (formatos suportados: {'{{1}}'}, {'${variavel}'} e [VARIAVEL]). Se o template tem variáveis e elas não foram detectadas, verifique o texto do template no painel ÁtomosChat.
                        </span>
                      )}
                    </p>
                    <div className="grid gap-2 max-h-60 overflow-auto">
                      {Array.from({ length: preview.expectedVariables }, (_, i) => i + 1).map((v) => {
                        const pname = preview.parameterNames?.[v - 1];
                        const label = pname ? `${v}. ${pname}` : `{{${v}}}`;
                        return (
                          <div key={v} className="flex items-center gap-2">
                            <span className="w-32 truncate" title={label}>{label}</span>
                            <Select
                              value={Object.keys(columnMapping).find((k) => columnMapping[k] === v) || ''}
                              onValueChange={(col) => setColumnMapping((prev) => ({ ...prev, [col]: v }))}
                            >
                              <SelectTrigger className="w-48"><SelectValue placeholder="Coluna" /></SelectTrigger>
                              <SelectContent>
                                {varColumns.map((h) => (
                                  <SelectItem key={h} value={h}>{h}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="mr-2 h-4 w-4" /> Voltar</Button>
                    <Button onClick={() => setStep(4)}>Próximo: Confirmar</Button>
                  </div>
                </>
              )}

              {/* Step 4: Confirmar */}
              {step === 4 && (
                <>
                  <p className="text-sm">Total de linhas: {preview?.totalRows}. Variáveis mapeadas. Clique em Criar disparo.</p>
                  <div className="flex gap-4">
                    <Button variant="outline" onClick={() => setStep(3)}><ArrowLeft className="mr-2 h-4 w-4" /> Voltar</Button>
                    <Button onClick={handleCreate} disabled={creating}>
                      {creating ? 'Criando...' : 'Criar disparo'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        ) : null}

        {/* Lista de disparos */}
        {!showWizard && (
          <>
            {/* Dashboard / Estatísticas */}
            {(list.length > 0 || listTotal > 0) && (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total de lotes</CardTitle>
                    <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{listTotal}</div>
                    <p className="text-xs text-muted-foreground">disparos avulsos (exibindo últimos {list.length})</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total de contatos</CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {list.reduce((acc, d) => acc + (Number(d.total_contatos) || 0), 0).toLocaleString('pt-BR')}
                    </div>
                    <p className="text-xs text-muted-foreground">na soma dos lotes</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Enviados</CardTitle>
                    <CheckCircle className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {list.reduce((acc, d) => acc + (Number(d.enviados) || 0), 0).toLocaleString('pt-BR')}
                    </div>
                    <p className="text-xs text-muted-foreground">mensagens enviadas</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Falhas</CardTitle>
                    <AlertCircle className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-destructive">
                      {list.reduce((acc, d) => acc + (Number(d.falhas) || 0), 0).toLocaleString('pt-BR')}
                    </div>
                    <p className="text-xs text-muted-foreground">erros de envio</p>
                  </CardContent>
                </Card>
              </div>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Disparos avulsos</CardTitle>
                <CardDescription>Lista de disparos criados. Use os filtros para refinar.</CardDescription>
                {/* Filtros */}
                <div className="flex flex-wrap gap-4 items-center pt-2">
                  {isAdmin && (
                    <div className="flex items-center gap-2">
                      <Label className="text-sm whitespace-nowrap">Cliente</Label>
                      <Select
                        value={listFilterClientId ? String(listFilterClientId) : 'all'}
                        onValueChange={(v) => setListFilterClientId(v === 'all' ? '' : Number(v))}
                        disabled={loadingClients}
                      >
                        <SelectTrigger className="w-[220px]">
                          <SelectValue placeholder={loadingClients ? 'Carregando...' : 'Todos os clientes'} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos os clientes</SelectItem>
                          {clients.map((c) => (
                            <SelectItem key={c.id} value={String(c.id)}>
                              {(c as any).trade_name || (c as any).legal_name || `Cliente ${c.id}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Label className="text-sm whitespace-nowrap">Status</Label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-[160px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="RASCUNHO">Rascunho</SelectItem>
                        <SelectItem value="ENVIANDO">Enviando</SelectItem>
                        <SelectItem value="CONCLUIDO">Concluído</SelectItem>
                        <SelectItem value="CANCELADO">Cancelado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p>Carregando...</p>
                ) : list.length === 0 ? (
                  <p className="text-muted-foreground">Nenhum disparo com os filtros aplicados. Ajuste os filtros ou crie um novo disparo.</p>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>ID</TableHead>
                          {isAdmin && <TableHead>Cliente</TableHead>}
                          <TableHead>Template</TableHead>
                          <TableHead>Conexão</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Total / Enviados / Falhas</TableHead>
                          <TableHead>Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {list.map((d) => (
                          <TableRow key={d.id}>
                            <TableCell>{d.id}</TableCell>
                            {isAdmin && (
                              <TableCell className="text-muted-foreground">
                                {d.client?.trade_name || d.client?.legal_name || `#${d.client_id}`}
                              </TableCell>
                            )}
                            <TableCell>{d.template_name}</TableCell>
                            <TableCell>
                              {d.whatsappConnection?.name || (d.atomos_template_id ? 'ÁtomosChat (V2)' : '-')}
                            </TableCell>
                            <TableCell>
                              <Badge variant={d.status === 'CONCLUIDO' ? 'default' : d.status === 'CANCELADO' ? 'secondary' : 'outline'}>
                                {d.status}
                              </Badge>
                            </TableCell>
                            <TableCell>{d.total_contatos} / {d.enviados} / {d.falhas}</TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm" onClick={() => openDetailModal(d)}>Detalhe</Button>
                              <Button variant="outline" size="sm" className="ml-2" onClick={() => openExportModal(d)}>
                                <Download className="h-4 w-4 mr-1" /> Exportar
                              </Button>
                              {d.status === 'RASCUNHO' && (
                                <Button variant="default" size="sm" className="ml-2" onClick={() => handleIniciar(d.id)}>
                                  <Play className="h-4 w-4 mr-1" /> Iniciar
                                </Button>
                              )}
                              {d.status === 'ENVIANDO' && (
                                <Button variant="secondary" size="sm" className="ml-2" onClick={() => handlePausar(d.id)}>
                                  <Pause className="h-4 w-4 mr-1" /> Pausar
                                </Button>
                              )}
                              {(d.status === 'PAUSADO' || d.status === 'CANCELADO') && (
                                <Button variant="default" size="sm" className="ml-2" onClick={() => handleRetomar(d.id)}>
                                  <Play className="h-4 w-4 mr-1" /> Retomar
                                </Button>
                              )}
                              {(d.status === 'CONCLUIDO' || d.status === 'CANCELADO' || d.status === 'PAUSADO') && d.falhas > 0 && (
                                <Button variant="outline" size="sm" className="ml-2" onClick={() => handleReenviarFalhas(d.id)}>
                                  <RefreshCw className="h-4 w-4 mr-1" /> Reenviar falhas
                                </Button>
                              )}
                              {d.status !== 'CONCLUIDO' && d.status !== 'CANCELADO' && (
                                <Button variant="outline" size="sm" className="ml-2" onClick={() => handleCancelar(d.id)}>
                                  <XCircle className="h-4 w-4 mr-1" /> Cancelar
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {listTotal > LIST_PAGE_SIZE && (
                      <div className="flex justify-center pt-4">
                        <Button variant="outline" onClick={openVerMaisModal}>
                          Ver mais ({listTotal} disparos)
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Modal de detalhe do disparo (substitui o card inline antigo) */}
            <DisparoAvulsoDetalheModal
              isOpen={showDetailModal}
              onClose={() => setShowDetailModal(false)}
              disparo={selectedDisparo}
              clientId={selectedDisparo?.client_id ?? effectiveClientId}
              isAdmin={isAdmin}
            />

            {/* Modal de exportação acionado pelo botão "Exportar" da tabela */}
            <ExportarDisparoAvulsoModal
              isOpen={!!exportTarget}
              onClose={() => setExportTarget(null)}
              disparo={exportTarget}
              clientId={exportTarget?.client_id ?? effectiveClientId}
            />

            {/* Modal Ver mais – histórico completo com 50 por página */}
            <Dialog open={showVerMaisModal} onOpenChange={setShowVerMaisModal}>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader>
                  <DialogTitle>Histórico de disparos avulsos</DialogTitle>
                </DialogHeader>
                <div className="flex-1 overflow-auto min-h-0">
                  {verMaisLoading ? (
                    <p className="py-8 text-center text-muted-foreground">Carregando...</p>
                  ) : (
                    <>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>ID</TableHead>
                            {isAdmin && <TableHead>Cliente</TableHead>}
                            <TableHead>Template</TableHead>
                            <TableHead>Conexão</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Total / Enviados / Falhas</TableHead>
                            <TableHead>Ações</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {verMaisList.map((d) => (
                            <TableRow key={d.id}>
                              <TableCell>{d.id}</TableCell>
                              {isAdmin && (
                                <TableCell className="text-muted-foreground">
                                  {d.client?.trade_name || d.client?.legal_name || `#${d.client_id}`}
                                </TableCell>
                              )}
                              <TableCell>{d.template_name}</TableCell>
                              <TableCell>
                              {d.whatsappConnection?.name || (d.atomos_template_id ? 'ÁtomosChat (V2)' : '-')}
                            </TableCell>
                              <TableCell>
                                <Badge variant={d.status === 'CONCLUIDO' ? 'default' : d.status === 'CANCELADO' ? 'secondary' : 'outline'}>
                                  {d.status}
                                </Badge>
                              </TableCell>
                              <TableCell>{d.total_contatos} / {d.enviados} / {d.falhas}</TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setShowVerMaisModal(false);
                                    openDetailModal(d);
                                  }}
                                >
                                  Detalhe
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="ml-2"
                                  onClick={() => openExportModal(d)}
                                >
                                  <Download className="h-4 w-4 mr-1" /> Exportar
                                </Button>
                                {d.status === 'RASCUNHO' && (
                                  <Button variant="default" size="sm" className="ml-2" onClick={() => handleIniciar(d.id)}>
                                    <Play className="h-4 w-4 mr-1" /> Iniciar
                                  </Button>
                                )}
                                {d.status === 'ENVIANDO' && (
                                  <Button variant="secondary" size="sm" className="ml-2" onClick={() => handlePausar(d.id)}>
                                    <Pause className="h-4 w-4 mr-1" /> Pausar
                                  </Button>
                                )}
                                {(d.status === 'PAUSADO' || d.status === 'CANCELADO') && (
                                  <Button variant="default" size="sm" className="ml-2" onClick={() => handleRetomar(d.id)}>
                                    <Play className="h-4 w-4 mr-1" /> Retomar
                                  </Button>
                                )}
                                {(d.status === 'CONCLUIDO' || d.status === 'CANCELADO' || d.status === 'PAUSADO') && d.falhas > 0 && (
                                  <Button variant="outline" size="sm" className="ml-2" onClick={() => handleReenviarFalhas(d.id)}>
                                    <RefreshCw className="h-4 w-4 mr-1" /> Reenviar falhas
                                  </Button>
                                )}
                                {d.status !== 'CONCLUIDO' && d.status !== 'CANCELADO' && (
                                  <Button variant="outline" size="sm" className="ml-2" onClick={() => handleCancelar(d.id)}>
                                    <XCircle className="h-4 w-4 mr-1" /> Cancelar
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {verMaisTotal > VER_MAIS_PAGE_SIZE && (
                        <div className="flex items-center justify-between gap-4 pt-4 border-t mt-4">
                          <p className="text-sm text-muted-foreground">
                            Página {verMaisPage} de {Math.ceil(verMaisTotal / VER_MAIS_PAGE_SIZE) || 1} ({verMaisTotal} disparos)
                          </p>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={verMaisPage <= 1 || verMaisLoading}
                              onClick={() => loadVerMaisPage(verMaisPage - 1)}
                            >
                              <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={verMaisPage >= Math.ceil(verMaisTotal / VER_MAIS_PAGE_SIZE) || verMaisLoading}
                              onClick={() => loadVerMaisPage(verMaisPage + 1)}
                            >
                              Próxima <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </Layout>
  );
}
